import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { apiPath } from './api';
import ServerErrorBanner, { useServerErrors } from './ServerErrorBanner';
import TradingCardWhen from './TradingCardWhen';
import { formatIstDateTimeLive, formatActivityWhen, formatCardOpenedAt, formatCardSoldAt } from './ist-datetime';
import './TradingActionBoard.css';
import IstClock from './IstClock';
import DemoModeBar from './DemoModeBar';
import {
  POOL_CAP,
  MAX_POSITIONS,
  SLOT_CAPITAL,
  PER_SHARE_PROFIT,
  MAX_HOLD_MINUTES,
  MIN_WIN_SCORE,
  MAX_WIN_SCORE,
  computeSlotQty,
  getLiveMove,
  isProfitSellHit,
  formatHoldRemaining,
  classifyBuyTab,
  getFixedBotPrice
} from './two-hour-rules';
import './TwoHourTrading.css';

const sym = (s) => (s || '').replace('.NS', '');

function loadThSellTabChoices(date) {
  try {
    const raw = JSON.parse(localStorage.getItem(`th-sell-choice-${date}`) || '{}');
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const sk = String(k || '').replace('.NS', '');
      if (sk) out[sk] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveThSellTabChoices(date, choices) {
  try {
    localStorage.setItem(`th-sell-choice-${date}`, JSON.stringify(choices || {}));
  } catch { /* ignore */ }
}

function applyThSellTabChoices({ buy, hold, sell, sellTabChoices }) {
  const choices = sellTabChoices || {};
  const stocks = Object.keys(choices);
  if (!stocks.length) return { buy, hold, sell };

  let nextSell = sell.filter((item) => {
    if (!item.stock) return true;
    return !choices[sym(item.stock)]?.choice;
  });
  let nextHold = hold.filter((item) => {
    if (!item.stock) return true;
    const sk = sym(item.stock);
    const ch = choices[sk]?.choice;
    if (ch === 'buy') return false;
    if (ch === 'sold') return true;
    if (ch === 'hold' && !item.fromSellChoice) return false;
    return true;
  });
  const nextBuy = [...buy];
  const seenBuy = new Set(nextBuy.map((b) => sym(b.stock)).filter(Boolean));
  const seenHold = new Set(nextHold.map((h) => sym(h.stock)).filter(Boolean));

  for (const stock of stocks) {
    const userChoice = choices[stock];
    const ch = userChoice?.choice;
    if (!ch) continue;
    const sk = sym(stock);
    const src = sell.find((s) => sym(s.stock) === sk) || hold.find((h) => sym(h.stock) === sk);

    if (ch === 'hold' && !seenHold.has(sk)) {
      seenHold.add(sk);
      nextHold.push({
        ...(src || {}),
        stock: sk,
        qty: userChoice.qty ?? src?.qty,
        botPrice: userChoice.botPrice ?? src?.botPrice,
        reason: userChoice.sellReason || 'From sell tab — holding',
        holdLabel: 'FROM SELL',
        fromSellChoice: true,
        timeLeft: userChoice.timeLeft ?? src?.timeLeft
      });
    }

    if (ch === 'buy' && !seenBuy.has(sk)) {
      seenBuy.add(sk);
      nextBuy.push({
        ...(src || {}),
        stock: sk,
        qty: userChoice.qty ?? src?.qty,
        botPrice: userChoice.botPrice ?? src?.botPrice,
        reason: `Rebuy — ${userChoice.sellReason || 'from sell tab'}`,
        fromSellChoice: true,
        winRate: src?.winRate
      });
    }
  }

  return { buy: nextBuy, hold: nextHold, sell: nextSell };
}

const STATUS_LABEL = {
  running: 'Live — 2 Hour Mode',
  waiting: 'Waiting for Market',
  market_closed: 'Market Closed',
  weekend: 'Weekend',
  disabled: 'Disabled',
  idle: 'Connecting…'
};

function formatIstClock(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }
  return String(value);
}

function playSellBuzz() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 1.0;
    master.connect(ctx.destination);
    const t0 = ctx.currentTime;
    for (let i = 0; i < 18; i += 1) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = i % 2 === 0 ? 920 : 680;
      g.gain.setValueAtTime(0.95, t0 + i * 0.14);
      g.gain.exponentialRampToValueAtTime(0.01, t0 + i * 0.14 + 0.11);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + i * 0.14);
      osc.stop(t0 + i * 0.14 + 0.12);
    }
    setTimeout(() => { ctx.close().catch(() => {}); }, 3200);
  } catch { /* ignore */ }
}

function playSellVoice(stock) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const text = `Sell now! ${stock || 'stock'}. Twenty paise profit per share reached. Go to Groww and sell.`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-IN';
    utter.rate = 0.95;
    utter.pitch = 1.05;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch { /* ignore */ }
}

function fireSellPopup(stock, move) {
  if (!('Notification' in window)) return;
  const title = '2 Hours Trading — SELL NOW';
  const body = `${stock} hit +₹${(move ?? PER_SHARE_PROFIT).toFixed(2)}/sh (target ₹${PER_SHARE_PROFIT}) · Sell on Groww now`;
  const show = () => {
    try {
      new Notification(title, { body, tag: 'twohour-sell', requireInteraction: true });
    } catch { /* ignore */ }
  };
  if (Notification.permission === 'granted') show();
  else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((p) => { if (p === 'granted') show(); });
  }
}

function InvestmentStockCard({ row, liveMap }) {
  const live = row.livePrice ?? liveMap[row.stock]?.price ?? null;
  const fixedPx = row.fixedBotPrice;
  const move = row.move ?? (fixedPx != null && live != null ? getLiveMove(fixedPx, live) : null);
  const profitHit = row.profitHit || (row.status === 'open' && isProfitSellHit(fixedPx, live));
  const holdRem = row.openedAt ? formatHoldRemaining(row.openedAt) : null;
  const statusLabel = row.status === 'pending'
    ? 'READY TO BUY'
    : row.status === 'sold'
      ? 'SOLD'
      : profitHit
        ? 'SELL NOW'
        : row.holdExpired
          ? '2 HR DONE'
          : 'HOLDING';
  const tabClass = row.tab || (row.status === 'pending' ? 'buy' : row.status === 'sold' ? 'sold' : 'hold');

  return (
    <article
      className={`th-invested-card ${row.status} ${profitHit ? 'profit-hit target-blink window-glow' : ''} ${row.holdExpired ? 'hold-expired' : ''}`}
    >
      <div className="th-invested-card-top">
        <strong>{row.stock}</strong>
        <span className={`th-inv-tab ${tabClass}`}>{statusLabel}</span>
      </div>
      <p className="th-invested-happening">{row.whatHappening || row.pickReason || '—'}</p>
      <hr className="th-card-divider tab-card-divider" aria-hidden="true" />
      {row.status !== 'pending' && <TradingCardWhen item={row} prefix="Bought" />}
      <div className="th-invested-amount">
        <span>Deployed</span>
        <strong>₹{(row.invested ?? POOL_CAP).toLocaleString('en-IN')}</strong>
      </div>
      <div className="th-invested-row">
        <span>Qty</span>
        <strong>×{row.qty || computeSlotQty(row.fixedBotPrice)}</strong>
      </div>
      {fixedPx != null && (
        <div className="th-invested-row">
          <span>Bot buy (fixed)</span>
          <strong>₹{Number(fixedPx).toFixed(2)}</strong>
        </div>
      )}
      {row.targetPrice != null && (
        <div className="th-invested-row">
          <span>Sell target</span>
          <strong>₹{Number(row.targetPrice).toFixed(2)} (+₹{PER_SHARE_PROFIT}/sh)</strong>
        </div>
      )}
      {row.status === 'open' && live != null && (
        <div className="th-invested-row">
          <span>Live now</span>
          <strong className={profitHit ? 'green target-blink-price' : ''}>₹{Number(live).toFixed(2)}</strong>
        </div>
      )}
      {move != null && row.status !== 'pending' && (
        <div className={`th-invested-move ${move >= 0 ? 'up' : 'down'}`}>
          {move >= 0 ? '+' : ''}₹{move.toFixed(2)}/sh
          {profitHit ? ' · SELL NOW ON GROWW' : row.status === 'open' ? ` · need +₹${PER_SHARE_PROFIT}/sh` : ''}
        </div>
      )}
      {row.status === 'sold' && row.sellPrice != null && (
        <div className="th-invested-row">
          <span>Sold @</span>
          <strong>₹{row.sellPrice.toFixed(2)}</strong>
          {row.pnl != null && (
            <em className={row.pnl >= 0 ? 'green' : 'red'}>
              P&amp;L {row.pnl >= 0 ? '+' : ''}₹{row.pnl.toFixed(2)}
            </em>
          )}
        </div>
      )}
      <div className="th-invested-meta">
        {row.status === 'pending' && row.winScore != null && <span>Win ~{row.winScore}%</span>}
        {holdRem && row.status === 'open' && <span>{holdRem.expired ? '2 hr passed' : holdRem.label}</span>}
        {row.winScore != null && row.status !== 'pending' && <span>Win {row.winScore}%</span>}
      </div>
    </article>
  );
}

function rowToActionItem(row, liveMap) {
  const live = row.livePrice ?? liveMap[row.stock]?.price ?? null;
  const fixedPx = row.fixedBotPrice;
  const move = row.move ?? (fixedPx != null && live != null ? getLiveMove(fixedPx, live) : null);
  const holdRem = row.openedAt ? formatHoldRemaining(row.openedAt) : null;
  return {
    stock: row.stock,
    qty: row.qty || computeSlotQty(fixedPx),
    botPrice: fixedPx,
    livePrice: live,
    targetPrice: row.targetPrice,
    move,
    winRate: row.winScore,
    shareTag: row.status === 'sold' ? 'old' : 'new',
    buyTimeLabel: row.dateTime || formatCardOpenedAt(row) || row.buyTime || null,
    sellTimeLabel: formatCardSoldAt(row) || row.sellTime,
    timeLeft: holdRem?.label || (row.holdRemaining ?? null),
    holdExpired: row.holdExpired || holdRem?.expired,
    holdLabel: row.holdExpired ? '2 HR DONE — HOLD' : (row.status === 'open' ? 'WITHIN 2 HR' : null),
    profitHit: row.profitHit || (row.status === 'open' && isProfitSellHit(fixedPx, live)),
    reason: row.whatHappening || row.pickReason || row.reason || '—',
    soldToday: row.status === 'sold',
    liveAlert: row.tab === 'sell' && row.status === 'open',
    kind: row.tab === 'sell' ? 'profit' : row.status
  };
}

function ActionCard({ item, tone, onSellTabChoice }) {
  const profitHit = item.profitHit && !item.soldToday;
  const showSellActions = (tone === 'sell' || tone === 'sold') && onSellTabChoice;
  return (
    <div className={`th-card ${tone} ${profitHit ? 'profit-hit target-blink window-glow' : ''} ${item.holdExpired ? 'hold-expired' : ''}`}>
      <div className="th-card-top">
        <strong>{item.stock}</strong>
        <span>×{item.qty}</span>
      </div>
      <div className="th-card-meta">
        {item.shareTag && <span className={`th-badge ${item.shareTag}`}>{item.shareTag === 'new' ? 'NEW SHARE' : 'OLD SHARE'}</span>}
        {item.holdLabel && <span className="th-badge hold-label">{item.holdLabel}</span>}
      </div>
      <hr className="th-card-divider tab-card-divider" aria-hidden="true" />
      <TradingCardWhen item={item} prefix={item.soldToday ? 'Sold' : 'Bought'} />
      {item.botPrice != null && (
        <div className="th-card-prices">
          <span className="th-bot-fixed">Bot buy (fixed) ₹{Number(item.botPrice).toFixed(2)}</span>
          {item.livePrice != null && (
            <strong className={`th-live-now ${profitHit ? 'target-blink-price' : ''}`}>
              Live ₹{Number(item.livePrice).toFixed(2)}
            </strong>
          )}
          {item.targetPrice != null && <span>Sell @ ₹{Number(item.targetPrice).toFixed(2)}</span>}
        </div>
      )}
      {item.move != null && (
        <div className={`th-card-move ${profitHit ? 'hit' : item.move >= 0 ? 'up' : 'down'}`}>
          {item.move >= 0 ? '+' : ''}₹{item.move.toFixed(2)}/sh
          {profitHit ? ' · SELL NOW ON GROWW' : ''}
        </div>
      )}
      {item.timeLeft && <div className="th-card-time">{item.timeLeft}</div>}
      <p className="th-card-reason">{item.reason}</p>
      {item.winRate != null && <span className="th-card-win">Win ~{item.winRate}%</span>}
      {showSellActions && (
        <div className="th-sell-actions" role="group" aria-label={`Choose action for ${item.stock}`}>
          <button type="button" className="th-sell-btn sold" onClick={() => onSellTabChoice(item, 'sold')}>
            SOLD
          </button>
          <button type="button" className="th-sell-btn hold" onClick={() => onSellTabChoice(item, 'hold')}>
            HOLD
          </button>
          <button type="button" className="th-sell-btn buy" onClick={() => onSellTabChoice(item, 'buy')}>
            BUY
          </button>
        </div>
      )}
    </div>
  );
}

export default function TwoHourTrading() {
  const [status, setStatus] = useState(null);
  const [livePrices, setLivePrices] = useState({ prices: {}, prevPrices: {}, status: 'idle' });
  const [clock, setClock] = useState('');
  const [liveClock, setLiveClock] = useState(0);
  const [sellTabChoices, setSellTabChoices] = useState({});
  const [sellChoiceToast, setSellChoiceToast] = useState('');
  const { errors: serverErrors, pushError, clearSource, dismiss, clearAll } = useServerErrors();
  const sellProfitPrevRef = useRef({});
  const sellBuzzIntervalRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [st, lp] = await Promise.all([
        axios.get(apiPath('/api/twohour/status')),
        axios.get(apiPath('/api/twohour/live-prices'))
      ]);
      setStatus(st.data);
      setLivePrices({
        prices: lp.data.prices || {},
        prevPrices: lp.data.prevPrices || {},
        status: 'live',
        updatedAt: lp.data.updatedAt
      });
      clearSource('twohour');
    } catch (e) {
      pushError('twohour', e);
    }
  }, [pushError, clearSource]);

  useEffect(() => {
    axios.post(apiPath('/api/twohour/enable')).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 1500);
    return () => clearInterval(iv);
  }, [refresh]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const unlock = () => { audioUnlockedRef.current = true; };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      setClock(formatIstDateTimeLive());
      setLiveClock(Date.now());
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);

  const pos = status?.currentPosition;
  const positions = status?.currentPositions || (pos ? [pos] : []);
  const topPick = status?.topPick;
  const liveMap = livePrices.prices || {};
  const investedStocks = status?.investedStocks || [];
  const todayKey = status?.todayDate || new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setSellTabChoices(loadThSellTabChoices(todayKey));
  }, [todayKey]);

  const handleSellTabChoice = useCallback((item, choice) => {
    const stock = sym(item.stock);
    setSellTabChoices((prev) => {
      const next = {
        ...prev,
        [stock]: {
          choice,
          sellReason: item.reason || '',
          timeLeft: item.timeLeft || null,
          qty: item.qty,
          botPrice: item.botPrice ?? item.price ?? null,
          at: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })
        }
      };
      saveThSellTabChoices(todayKey, next);
      return next;
    });
    const labels = { sold: 'marked SOLD — removed from Sell tab', hold: 'moved to Hold tab', buy: 'moved to Buy tab (rebuy)' };
    setSellChoiceToast(`${stock}: ${labels[choice] || choice}`);
    window.setTimeout(() => setSellChoiceToast(''), 3500);
  }, [todayKey]);

  const actionLists = useMemo(() => {
    const buy = [];
    const hold = [];
    const sell = [];
    const liveMapLocal = livePrices.prices || {};

    for (const row of investedStocks) {
      const item = rowToActionItem(row, liveMapLocal);
      if (row.status === 'pending' || row.tab === 'buy') {
        buy.push(item);
      } else if (row.tab === 'sell' || (row.status === 'open' && item.profitHit)) {
        sell.push({ ...item, liveAlert: true });
      } else if (row.status === 'sold') {
        sell.push(item);
      } else if (row.status === 'open') {
        hold.push(item);
      }
    }

    const openCount = investedStocks.filter((r) => r.status === 'open').length;
    if (!buy.length && openCount < (status?.maxPositions ?? MAX_POSITIONS)) {
      const buyInfo = classifyBuyTab({
        positionsCount: openCount,
        maxPositions: status?.maxPositions ?? MAX_POSITIONS,
        topPick
      });
      if (buyInfo.buy && topPick) {
        const sk = sym(topPick.symbol);
        const px = topPick.price;
        const qty = computeSlotQty(px);
        buy.push({
          stock: sk,
          qty,
          botPrice: px,
          livePrice: liveMapLocal[sk]?.price ?? px,
          targetPrice: px != null ? Math.round((px + PER_SHARE_PROFIT) * 100) / 100 : null,
          winRate: topPick.rs1PredictScore ?? topPick.winRate,
          shareTag: 'new',
          reason: buyInfo.reason || topPick.pickReason
        });
      }
    }

    return applyThSellTabChoices({ buy, hold, sell, sellTabChoices });
  }, [investedStocks, topPick, livePrices.prices, status?.maxPositions, sellTabChoices]);

  const sellAlertsOpen = status?.sellAlertsOpen !== false;

  const liveSellAlerts = sellAlertsOpen
    ? actionLists.sell.filter(
        (s) => s.liveAlert && s.profitHit && !s.soldToday && !sellTabChoices[sym(s.stock)]?.choice
      )
    : [];

  useEffect(() => {
    if (!sellAlertsOpen) {
      if (sellBuzzIntervalRef.current) {
        clearInterval(sellBuzzIntervalRef.current);
        sellBuzzIntervalRef.current = null;
      }
      return;
    }
    const prev = sellProfitPrevRef.current;
    for (const item of liveSellAlerts) {
      if (!prev[item.stock]) {
        playSellBuzz();
        playSellVoice(item.stock);
        fireSellPopup(item.stock, item.move);
      }
      prev[item.stock] = true;
    }
    for (const k of Object.keys(prev)) {
      if (!liveSellAlerts.some((a) => a.stock === k)) delete prev[k];
    }
    if (liveSellAlerts.length && !sellBuzzIntervalRef.current) {
      sellBuzzIntervalRef.current = setInterval(() => {
        liveSellAlerts.forEach((item) => {
          playSellBuzz();
          playSellVoice(item.stock);
        });
      }, 12000);
    } else if (!liveSellAlerts.length && sellBuzzIntervalRef.current) {
      clearInterval(sellBuzzIntervalRef.current);
      sellBuzzIntervalRef.current = null;
    }
  }, [liveSellAlerts, liveClock, sellAlertsOpen]);

  useEffect(() => () => {
    if (sellBuzzIntervalRef.current) clearInterval(sellBuzzIntervalRef.current);
  }, []);

  const liveSellAlert = liveSellAlerts.length > 0;
  const activeStep = liveSellAlert
    ? 2
    : actionLists.buy.length > 0
      ? 1
      : 3;

  const totalPnl = status?.totalDayPnl ?? 0;
  const st = status?.status || 'idle';

  return (
    <div className="th-page">
      <ServerErrorBanner errors={serverErrors} onDismiss={dismiss} onClearAll={clearAll} />

      <DemoModeBar
        mode="twohour"
        demoMode={!!status?.demoMode}
        marketClosed={['market_closed', 'order_window_closed', 'weekend', 'disabled'].includes(st)}
        onChange={refresh}
      />

      <header className="th-header">
        <div>
          <h1>2 Hours Trading</h1>
          <p>
            ₹{POOL_CAP.toLocaleString('en-IN')} pool · {MAX_POSITIONS} stocks × ~₹{Math.round(SLOT_CAPITAL)} each · +₹{PER_SHARE_PROFIT}/share ·
            {' '}{MIN_WIN_SCORE}–{MAX_WIN_SCORE}% win · 2 hr hold · no stop loss
          </p>
        </div>
        <div className="th-status-pill">
          <span><IstClock /></span>
          <strong>{STATUS_LABEL[st] || st}</strong>
        </div>
      </header>

      {liveSellAlert && (
        <div className="th-sell-banner target-blink window-glow" role="alert">
          <strong>SELL NOW — {liveSellAlerts.map((s) => s.stock).join(', ')}</strong>
          <span>+₹{PER_SHARE_PROFIT}/share target hit · sell on Groww</span>
        </div>
      )}

      {sellChoiceToast && (
        <div className="th-choice-toast" role="status">{sellChoiceToast}</div>
      )}

      <section className="th-invested-panel th-all-stocks-panel">
        <header className="th-invested-head">
          <div>
            <h3>All stocks today — card view</h3>
            <p>
              Every slot ~₹{Math.round(SLOT_CAPITAL)} from ₹{POOL_CAP.toLocaleString('en-IN')} pool ·
              {' '}{status?.investedSummary?.openCount ?? 0} holding ·
              {' '}{status?.investedSummary?.soldCount ?? 0} sold ·
              {' '}{status?.investedSummary?.slotsFree ?? MAX_POSITIONS} slot(s) free ·
              {' '}{status?.investedSummary?.pendingCount ?? 0} ready to buy
            </p>
          </div>
          <em className="th-invested-count">{investedStocks.length} cards</em>
        </header>
        {investedStocks.length === 0 ? (
          <p className="th-empty th-invested-empty">
            No stocks yet — bot fills up to {MAX_POSITIONS} slots (~₹{Math.round(SLOT_CAPITAL)} each) from the ₹{POOL_CAP.toLocaleString('en-IN')} pool.
          </p>
        ) : (
          <div className="th-invested-grid th-all-stocks-grid">
            {investedStocks.map((row) => (
              <InvestmentStockCard key={row.id || `${row.stock}-${row.buyTime}`} row={row} liveMap={liveMap} />
            ))}
          </div>
        )}
      </section>

      <div className="th-stats">
        <div className="th-stat">
          <span>Pool</span>
          <strong className="purple">₹{POOL_CAP.toLocaleString('en-IN')}</strong>
        </div>
        <div className="th-stat">
          <span>Deployed</span>
          <strong>₹{(status?.poolDeployed ?? 0).toFixed(0)}</strong>
        </div>
        <div className="th-stat">
          <span>Target</span>
          <strong className="green">+₹{PER_SHARE_PROFIT}/sh</strong>
        </div>
        <div className="th-stat">
          <span>Hold window</span>
          <strong>{MAX_HOLD_MINUTES / 60} hours</strong>
        </div>
        <div className="th-stat">
          <span>Day P&amp;L</span>
          <strong className={totalPnl >= 0 ? 'green' : 'red'}>
            {totalPnl >= 0 ? '+' : ''}₹{totalPnl.toFixed(2)}
          </strong>
        </div>
        <div className="th-stat">
          <span>Stocks invested</span>
          <strong className="purple">{status?.investedSummary?.count ?? 0}</strong>
        </div>
        <div className="th-stat">
          <span>Total deployed today</span>
          <strong>₹{(status?.investedSummary?.totalInvested ?? 0).toLocaleString('en-IN')}</strong>
        </div>
        <div className="th-stat">
          <span>Slots</span>
          <strong>{status?.investedSummary?.slotsUsed ?? positions.length}/{status?.maxPositions ?? MAX_POSITIONS}</strong>
        </div>
        <div className="th-stat">
          <span>Per slot</span>
          <strong>~₹{Math.round(SLOT_CAPITAL)}</strong>
        </div>
        <div className="th-stat">
          <span>Stop loss</span>
          <strong>OFF</strong>
        </div>
      </div>

      <div className="th-board">
        <div className={`th-col buy ${activeStep === 1 ? 'active-col' : ''}`}>
          <div className="th-col-head">
            <span>🟢</span>
            <div>
              <strong>BUY</strong>
              <small>{MAX_POSITIONS} picks · ~₹{Math.round(SLOT_CAPITAL)} each · 70–90% win</small>
            </div>
          </div>
          <div className="th-col-body">
            {actionLists.buy.length === 0 ? (
              <p className="th-empty">
                {positions.length >= (status?.maxPositions ?? MAX_POSITIONS)
                  ? 'All slots filled — hold for +₹' + PER_SHARE_PROFIT + '/sh'
                  : 'Scanning for 70–90% win stocks to fill slots…'}
              </p>
            ) : (
              actionLists.buy.map((item) => <ActionCard key={`buy-${item.stock}`} item={item} tone="buy" />)
            )}
          </div>
        </div>

        <div className={`th-col hold ${activeStep === 3 ? 'active-col' : ''}`}>
          <div className="th-col-head">
            <span>🟡</span>
            <div>
              <strong>HOLD</strong>
              <small>2 hr window · no stop · keep if target not hit</small>
            </div>
          </div>
          <div className="th-col-body">
            {actionLists.hold.length === 0 ? (
              <p className="th-empty">No holds — bot position shows here after buy</p>
            ) : (
              actionLists.hold.map((item) => <ActionCard key={`hold-${item.stock}`} item={item} tone="hold" />)
            )}
          </div>
        </div>

        <div className={`th-col sell ${activeStep === 2 ? 'active-col' : ''} ${liveSellAlert ? 'sell-alert-col' : ''}`}>
          <div className="th-col-head">
            <span>🔴</span>
            <div>
              <strong>SELL</strong>
              <small>
                {liveSellAlert
                  ? `ALERT — +₹${PER_SHARE_PROFIT}/sh reached · sell on Groww`
                  : `Only when +₹${PER_SHARE_PROFIT}/share reached`}
              </small>
            </div>
          </div>
          <div className="th-col-body">
            {actionLists.sell.length === 0 ? (
              <p className="th-empty">No sells — hold until +₹{PER_SHARE_PROFIT}/sh</p>
            ) : (
              actionLists.sell.map((item) => (
                <ActionCard
                  key={`sell-${item.stock}-${item.soldToday ? 'old' : 'live'}`}
                  item={item}
                  tone={item.soldToday ? 'sold' : 'sell'}
                  onSellTabChoice={handleSellTabChoice}
                />
              ))
            )}
          </div>
          <p className="th-col-foot">After beep — tap SOLD / HOLD / BUY per stock · your choice on Groww</p>
        </div>
      </div>

      <section className="th-log">
        <h3>Activity log</h3>
        <ul>
          {(status?.activityLog || []).slice(0, 15).map((e, i) => (
            <li key={`${e.time}-${i}`}>
              <strong>{formatActivityWhen(e)}</strong> — {e.message}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
